import {
  ButtonProps,
  DialogDescription,
  HStack,
  Input,
  VStack,
} from "@chakra-ui/react";
import { ReactElement, useState } from "react";
import { FaCheck } from "react-icons/fa6";

import { RoundedButton } from "../rounded-button";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";

import { isPPDeprecated } from "@/constants/shared";
import { useAuth } from "@/hooks/useAuth";
import { useWalletAddressUserData } from "@/hooks/useWalletAddressUserData";
import { AmplitudeTrackingEvent, trackAmplitudeEvent } from "@/lib/amplitude";

export const InviteFriendsModal = ({
  triggerOverride,
  ...props
}: ButtonProps & { triggerOverride?: ReactElement }) => {
  const { address, isAuthenticated } = useAuth();
  const { stats } = useWalletAddressUserData(address);
  const { referralCode, referrals, referredByUser } = stats ?? {};
  const referralLink = `${window.location.origin}/?referrer=${referralCode}`;
  // const toast = useToast();
  const [copied, setCopied] = useState(false);

  if (!isAuthenticated || !referralCode) return null;

  const handleCopyLink = () => {
    trackAmplitudeEvent(AmplitudeTrackingEvent.PortalCopiedReferralLink, {});

    navigator.clipboard.writeText(referralLink);
    setCopied(true);

    // Reset after 2 seconds
    setTimeout(() => {
      setCopied(false);
    }, 2000);
  };

  const handleShareOnTwitter = () => {
    const tweetText =
      "I’m exploring #RWAfi on Plume Network. Check out my profile and sign up to earn Plume Points 🪶🪶🪶";
    const shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(`https://${referralLink}`)}`;
    window.open(shareUrl, "_blank");
  };
  if (isPPDeprecated) return null;

  return (
    <DialogRoot size="md">
      <DialogTrigger asChild>
        {triggerOverride ? (
          triggerOverride
        ) : (
          <RoundedButton h={12} variant="solid" {...props}>
            Invite Friends
          </RoundedButton>
        )}
      </DialogTrigger>
      <DialogContent px={2} borderRadius="3xl">
        <DialogHeader fontFamily="Lufga, sans-serif" mt={6} display="unset">
          <DialogTitle
            textAlign="center"
            fontSize="4xl"
            mb={4}
            fontWeight="700"
          >
            Invite Friends
          </DialogTitle>
          <DialogDescription
            textAlign="center"
            fontSize="xl"
            lineHeight="1.2"
            mb={4}
            letterSpacing={-0.5}
            fontWeight="500"
          >
            Refer friends to Plume and earn! Get 10% of the Plume Points your
            top 10 referrals earn.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <Input
            borderRadius="xl"
            value={referralLink}
            variant="subtle"
            textAlign="center"
            fontSize="lg"
            readOnly
            color="#787878"
            _focus={{ boxShadow: "none", borderWidth: "0", outline: "none" }}
            _hover={{ borderWidth: "0", outline: "none" }}
          />
          <HStack gap={4} mt={4}>
            <Input
              borderRadius="xl"
              value={`${referrals} users referred`}
              variant="subtle"
              disabled
              textAlign="center"
              readOnly
              _focus={{ boxShadow: "none" }}
              _disabled={{ opacity: 0.8, cursor: "default" }}
            />
            {referredByUser && (
              <Input
                borderRadius="xl"
                value={`Invited by ${referredByUser.walletAddress}`}
                variant="subtle"
                disabled
                textAlign="center"
                readOnly
                _focus={{ boxShadow: "none" }}
                _disabled={{ opacity: 0.8, cursor: "default" }}
              />
            )}
          </HStack>
          <VStack gap={4} mt={4}>
            <RoundedButton
              width="full"
              variant="outline"
              onClick={handleCopyLink}
              _focus={{
                boxShadow: "none",
                borderColor: "black",
                borderWidth: "1px",
                outline: "none",
              }}
              _active={{ bg: "transparent" }}
            >
              {copied ? (
                <>
                  <FaCheck fontSize="12px" /> Copied
                </>
              ) : (
                "Copy Invite Link"
              )}
            </RoundedButton>
            <RoundedButton
              width="full"
              onClick={handleShareOnTwitter}
              _focus={{
                boxShadow: "none",
                borderColor: "black",
                borderWidth: "1px",
                outline: "none",
              }}
              _active={{ bg: "bg.accent" }}
            >
              Share On Twitter
            </RoundedButton>
          </VStack>
        </DialogBody>
        <DialogCloseTrigger />
      </DialogContent>
    </DialogRoot>
  );
};
